import { useCallback, useEffect, useRef } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { applyGroupTransformToDescendants } from '@/lib/groups/shape-group-descendant-transform'
import { isDescendantOfGroup } from '@/lib/groups/shape-group-tree'
import { isGroupShape } from '@/lib/groups/group-kind'
import { shouldBlockShapeUpdate } from '@/lib/groups/element-edit-policy'
import type { EditGuardBypassOpts } from '@/lib/groups/types/edit-guard-bypass-opts'
import type { ShapeListOp } from '@/lib/shapes/types/shape-list-op'
import { applyShapeListOps as applyShapeListOpsPure } from '@/lib/shapes/shape-list-ops'
import {
  applyPartialShapeUpdate,
  dedupeShapesByIdPreserveOrder,
  getShapeById,
} from '../helpers/shape-list'
import type { ShapeManagerStateApi } from './use-shape-manager-state'

export function useShapeUpdates(ctx: ShapeManagerStateApi) {
  const { selectedShapeIdsRef, setSelectedShapeIds, setShapeBounds, setShapes, shapesRef } = ctx

  const updateShape = useCallback(
    (
      shapeId: string,
      updates: Partial<ShapeData>,
      opts?: EditGuardBypassOpts & { propagateGroupTransform?: boolean }
    ) => {
      const propagateGroupTransform = opts?.propagateGroupTransform !== false
      const listBefore = shapesRef.current
      const shapesById = new Map(listBefore.map((s) => [s.id, s]))
      const beforeShape = getShapeById(listBefore, shapeId)

      if (beforeShape && shouldBlockShapeUpdate(beforeShape, updates, shapesById, opts)) {
        return
      }

      const touchesGroupTransform =
        updates.x !== undefined ||
        updates.y !== undefined ||
        updates.width !== undefined ||
        updates.height !== undefined ||
        updates.rotation !== undefined

      const applyUpdate = (list: ShapeData[]): ShapeData[] => {
        const beforeShape = getShapeById(list, shapeId)
        let nextList = applyPartialShapeUpdate(list, shapeId, updates)

        if (
          !propagateGroupTransform ||
          !beforeShape ||
          !isGroupShape(beforeShape) ||
          !touchesGroupTransform
        ) {
          return nextList
        }

        const afterShape = getShapeById(nextList, shapeId)

        if (!afterShape) {
          return nextList
        }

        nextList = applyGroupTransformToDescendants(
          nextList,
          shapeId,
          beforeShape,
          afterShape,
          applyPartialShapeUpdate
        )
        return nextList
      }

      // React runs functional `setShapes` updaters on a later render; callers like the object
      // panel read `getShapes()` in the same event tick. Advance the ref immediately so reads match.
      shapesRef.current = applyUpdate(shapesRef.current)

      setShapes((prev) => {
        const nextList = applyUpdate(prev)

        shapesRef.current = nextList
        return nextList
      })
    },
    [setShapes, shapesRef]
  )

  const applyShapeHistoryPatches = useCallback(
    (patches: Array<{ shapeId: string; patch: Partial<ShapeData> }>) => {
      if (patches.length === 0) return

      let list = shapesRef.current
      // Build a stable ancestry map from the original list. parentGroupId links don't change
      // during patching (we only change x/y/width/height/rotation), so this snapshot is safe
      // for the whole loop.
      const byIdForAncestry = new Map(list.map((s) => [s.id, s]))
      const patchedIds = new Set(patches.map((p) => p.shapeId))

      for (const { shapeId, patch } of patches) {
        const beforeShape = list.find((s) => s.id === shapeId)

        list = applyPartialShapeUpdate(list, shapeId, patch)

        if (beforeShape && isGroupShape(beforeShape)) {
          const touchesGroupTransform =
            patch.x !== undefined ||
            patch.y !== undefined ||
            patch.width !== undefined ||
            patch.height !== undefined ||
            patch.rotation !== undefined

          if (touchesGroupTransform) {
            const afterShape = list.find((s) => s.id === shapeId)

            if (afterShape) {
              // When a group is dragged, the commit records explicit before/after for BOTH the
              // group and every descendant (via groupTransformInitials). If descendants are
              // already in the patches list, applying propagation on top of their direct patches
              // would move them twice (double-application). Only propagate when descendants are
              // absent from the batch — i.e. an alignment or multi-select-with-group move where
              // only the group shell position is in the history items.
              const hasExplicitDescendantPatches = patches.some((p) => {
                if (p.shapeId === shapeId) return false
                if (!patchedIds.has(p.shapeId)) return false

                const s = byIdForAncestry.get(p.shapeId)

                return s ? isDescendantOfGroup(s, shapeId, byIdForAncestry) : false
              })

              if (!hasExplicitDescendantPatches) {
                list = applyGroupTransformToDescendants(
                  list,
                  shapeId,
                  beforeShape,
                  afterShape,
                  applyPartialShapeUpdate
                )
              }
            }
          }
        }
      }

      list = dedupeShapesByIdPreserveOrder(list, 'applyShapeHistoryPatches')

      shapesRef.current = list
      setShapes(list)
    },
    [setShapes, shapesRef]
  )

  const applyShapeListOps = useCallback(
    (ops: ShapeListOp[]) => {
      if (ops.length === 0) return

      const next = dedupeShapesByIdPreserveOrder(
        applyShapeListOpsPure(shapesRef.current, ops),
        'applyShapeListOps'
      )

      shapesRef.current = next
      setShapes(next)

      const validIds = new Set(next.map((s) => s.id))

      setSelectedShapeIds((prev) => {
        const filtered = prev.filter((id) => validIds.has(id))

        selectedShapeIdsRef.current = filtered
        return filtered
      })

      setShapeBounds((prev) => {
        let changed = false
        const out: typeof prev = {}

        for (const [id, bounds] of Object.entries(prev)) {
          if (validIds.has(id)) {
            out[id] = bounds
          } else {
            changed = true
          }
        }

        return changed ? out : prev
      })
    },
    [selectedShapeIdsRef, setSelectedShapeIds, setShapeBounds, setShapes, shapesRef]
  )

  const updateShapeRef = useRef(updateShape)

  useEffect(() => {
    updateShapeRef.current = updateShape
  }, [updateShape])

  return {
    updateShape,
    applyShapeHistoryPatches,
    applyShapeListOps,
    updateShapeRef,
  }
}

export type ShapeUpdatesApi = ReturnType<typeof useShapeUpdates>
